import {ErrorPage} from 'components/errors/ErrorPage';
import {setDoc} from 'firebase/firestore';
import {useUserId} from 'hooks/user/useUserId';
import {LandingNav} from 'landing/components/LandingNav';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import {Loading} from 'components/loading/Loading';
import {useNavigate, useParams} from 'react-router-dom';
import Swal from 'sweetalert2';
import {DebuggingForm} from './DebuggingForm';
import {getApplicationDataRef} from 'course/application/firebase/models/applicationData';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {
  getApplicantDataRef,
  SPECIAL_FIELD_NAMES,
} from 'course/application/firebase/models/applicantData';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useMemo} from 'react';

export const Debugging = () => {
  return (
    <div>
      <LandingNav applicationType={'sectionLeader'} subpage={'Debugging'} />
      <div>
        <DebuggingInner />
      </div>
    </div>
  );
};

const DebuggingInner = () => {
  const navigate = useNavigate();
  const userId = useUserId();
  const {targetCourseId} = useParams();
  const courseId = useCourseId();
  const t = useTranslateFunction('application');

  // We need to memoize the document reference since otherwise the converter is
  // sensitive to the form format and consequently causes the document data to
  // be constantly re-fetched, producing a blinking effect.
  const slAppDocRef = useMemo(
    () =>
      getApplicantDataRef({
        userId,
        courseId: targetCourseId,
        applicationType: 'sl',
      }),
    [userId, targetCourseId],
  );
  const [slAppData, slAppDataLoading, slAppDataError] =
    useDocumentDataOnce(slAppDocRef);

  const applicationDataRef = getApplicationDataRef(targetCourseId, 'sl');
  const [applicationData, applicationDataLoading, applicationDataError] =
    useDocumentDataOnce(applicationDataRef);

  if (slAppDataLoading || applicationDataLoading) {
    return <Loading />;
  }
  if (slAppDataError || applicationDataError) {
    return (
      <ErrorPage msg="Error loading application. Please try again later." />
    );
  }

  const appHomeUrl = `/${courseId}/applyteach/${targetCourseId}`;

  const safeAppData = slAppData || {fields: []};
  const debugData = applicationData?.debugInfo || [];

  async function onSubmit(data: Record<string, string>) {
    // Create a dynamic object based on the debugData structure
    const debugAnswers: Record<string, string> = {};

    // Loop through debugData and extract corresponding answers from form data
    debugData.forEach(debugItem => {
      if (data[debugItem.title]) {
        debugAnswers[debugItem.title] = data[debugItem.title];
      }
    });

    await setDoc(
      slAppDocRef,
      {
        [SPECIAL_FIELD_NAMES.DEBUG_ANSWERS]: debugAnswers,
      },
      {merge: true},
    );

    Swal.fire({
      title: String(t('Success')),
      heightAuto: false,
      text: String(t('Your debugging answers have been recorded!')),
      icon: 'success',
      preConfirm: () => {
        navigate(appHomeUrl);
      },
    });
  }

  return (
    <DebuggingForm
      serverData={safeAppData}
      onSubmit={onSubmit}
      backUrl={appHomeUrl}
      debugData={debugData}
    />
  );
};
